import React, { useState, useCallback, useEffect , useRef } from 'react';
import { orderBy, map, compose } from 'lodash/fp';
import { DraggableUpdateSectionForm } from './forms';
import { Section, SectionWithOrder } from '../../../../server-types';
import { useSilentMutation } from '../../../../graphql/hooks';
import { UPDATE_SECTION_ORDER } from '../../graphql';

interface SectionListViewProps {
    sections: Section[];
}

const defineChange = (arr1: string[], arr2: string[]) => {
    if(arr1.length !== arr2.length){
        // we have an error
    }

    const diff: SectionWithOrder[] = [];

    arr2.forEach((val, indx) => {
        const newId = arr1[indx];
        if(newId !== val){
            diff.push({ id: newId, ordering: indx });
        }
    });

    return diff;
};

const getSectionKeys = (sections: Section[]) => {
    const keyedSections: Record<string, Section> = {};
    const sectionKeys = compose<[Section[]], Section[], string[]>(
        map(section => {
            keyedSections[section.id] = section;
            return section.id;
        }),
        orderBy<Section>('ordering', 'asc'),
    )(sections);

    return { sectionKeys, keyedSections };
};

const SectionListView: React.FC<SectionListViewProps> = ({ sections }) => {
    const { keyedSections, sectionKeys } = getSectionKeys(sections);
    const [orderedSectionKeys, setOrderedSectionKeys] = useState(sectionKeys);
    const [shouldCommit, setShouldCommit] = useState(false);
    const sectionRef = useRef<string[] | null>(null);

    const [persistSectionOrder] = useSilentMutation(UPDATE_SECTION_ORDER);

    useEffect(() => {
        sectionRef.current = sectionKeys;
    }, [sections, sectionKeys]);

    useEffect(() => {
        const { sectionKeys: updatedSectionKeys } = getSectionKeys(sections);
        setOrderedSectionKeys(updatedSectionKeys);
    }, [sections]);

    useEffect(() => {
        if(shouldCommit && sectionRef.current){
            const orderedSections = defineChange(orderedSectionKeys, sectionRef.current);
            const updateOrdering = async () => {
                if(orderedSections.length > 0) {
                    try {
                        await persistSectionOrder({ variables: { orderedSections } });
                    } catch (error) {
                        // nothing we can do
                    }
                }

            };
            setShouldCommit(false);

            // eslint-disable-next-line no-void
            void updateOrdering();

        }
    }, [shouldCommit, sectionKeys, persistSectionOrder, orderedSectionKeys]);

    const moveSection = useCallback(
        (dragIndex: number, hoverIndex: number) => {
            const dragSection = orderedSectionKeys[dragIndex];
            const newKeys = [...orderedSectionKeys];
            newKeys.splice(dragIndex, 1);
            newKeys.splice(hoverIndex, 0, dragSection);

            setOrderedSectionKeys(newKeys);
        },
        [orderedSectionKeys],
    );

    return (
        <>
            {orderedSectionKeys.map((id, index) => {
                if(!keyedSections[id]) {
                    return null;
                }

                const { places, name } = keyedSections[id];
                const count = places? places.length : 0;

                return (
                    <DraggableUpdateSectionForm
                        key={id}
                        name={name}
                        id={id}
                        setShouldCommit={setShouldCommit}
                        count={count}
                        index={index}
                        moveSection={moveSection}
                    />
                );
            })}
        </>
    );
};


export default SectionListView;
